// T063: Transport System (搭乘系統)
// 處理運輸單位的搭乘、上車、下車機制

import type { GameState, Card, BattlefieldPosition } from '../../types';
import { getLogger } from '../../utils/logger';

const log = getLogger('TransportSystem');

/**
 * 運輸單位狀態
 */
export interface TransportState {
  /** 運輸單位 ID */
  unitId: string;
  /** 最大容量 */
  capacity: number;
  /** 已搭乘的單位 ID 列表 */
  passengers: string[];
}

/**
 * 搭乘結果
 */
export interface BoardResult {
  success: boolean;
  error?: 'TRANSPORT_FULL' | 'NOT_TRANSPORT' | 'INVALID_PASSENGER' | 'NOT_ON_FIELD';
  newState?: GameState;
}

/**
 * 下車結果
 */
export interface DisembarkResult {
  success: boolean;
  error?: 'NO_PASSENGERS' | 'NOT_ENOUGH_PASSENGERS' | 'NO_EMPTY_SLOTS';
  disembarkedUnits?: Card[];
  newState?: GameState;
}

/**
 * 解析搭乘容量
 * 例: "此卡被召喚/打出時搭乘3" → 3
 */
export function parseTransportCapacity(effectText: string): number | null {
  const match = effectText.match(/搭乘\s*(\d+)/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * 解析下車數量
 * 例: "可下車2代替破壞" → 2
 */
export function parseDisembarkCount(effectText: string): number | null {
  const match = effectText.match(/下車\s*(\d+)/);
  if (match) {
    return parseInt(match[1], 10);
  }
  return null;
}

/**
 * 檢查是否為運輸單位
 */
export function isTransportUnit(_card: Card, effectText?: string): boolean {
  if (!effectText) return false;
  return effectText.includes('搭乘') && /搭乘\s*\d+/.test(effectText);
}

/**
 * 檢查單位是否可以被搭乘（目前只有小旅人）
 */
export function canBePassenger(unit: Card): boolean {
  // 小旅人可以搭乘
  return unit.definitionId === 'little_traveler' || 
         unit.definitionId.includes('traveler') ||
         unit.definitionId.includes('小旅人');
}

/**
 * 取得運輸單位的搭乘狀態
 */
export function getTransportState(state: GameState, transportId: string): TransportState | null {
  // 從卡牌的 passengers 屬性取得
  const transport = findCardOnBattlefield(state, transportId);
  if (!transport) return null;
  
  return {
    unitId: transportId,
    capacity: transport.mergedCards?.length ?? 0, // 使用 mergedCards 儲存乘客
    passengers: transport.passengers?.map(p => p.id) ?? [],
  };
}

/**
 * 在戰場上尋找卡牌
 */
function findCardOnBattlefield(state: GameState, cardId: string): Card | null {
  const sides = [state.battlefield.player1Side, state.battlefield.player2Side];
  
  for (const side of sides) {
    for (const slot of [...side.frontLine, ...side.backLine]) {
      if (slot.unit?.id === cardId) {
        return slot.unit;
      }
    }
  }
  
  return null;
}

/**
 * 取得卡牌位置
 */
function getCardPosition(state: GameState, cardId: string): BattlefieldPosition | null {
  const sides: Array<{ key: 'player1' | 'player2'; side: typeof state.battlefield.player1Side }> = [
    { key: 'player1', side: state.battlefield.player1Side },
    { key: 'player2', side: state.battlefield.player2Side },
  ];
  
  for (const { key, side } of sides) {
    for (let i = 0; i < side.frontLine.length; i++) {
      if (side.frontLine[i].unit?.id === cardId) {
        return { side: key, row: 'front', column: (i + 1) as 1 | 2 | 3 | 4 | 5 };
      }
    }
    for (let i = 0; i < side.backLine.length; i++) {
      if (side.backLine[i].unit?.id === cardId) {
        return { side: key, row: 'back', column: (i + 1) as 1 | 2 | 3 | 4 | 5 };
      }
    }
  }
  
  return null;
}

/**
 * 執行搭乘（上車）
 */
export function boardUnit(
  state: GameState,
  passengerId: string,
  transportId: string,
  capacity: number
): BoardResult {
  const transport = findCardOnBattlefield(state, transportId);
  const passenger = findCardOnBattlefield(state, passengerId);
  
  if (!transport) {
    return { success: false, error: 'NOT_TRANSPORT' };
  }
  
  if (!passenger) {
    return { success: false, error: 'INVALID_PASSENGER' };
  }
  
  // 檢查容量
  const currentPassengers = transport.passengers ?? [];
  if (currentPassengers.length >= capacity) {
    return { success: false, error: 'TRANSPORT_FULL' };
  }
  
  // 執行搭乘：將乘客從戰場移除，加入運輸單位的 passengers
  const passengerPos = getCardPosition(state, passengerId);
  if (!passengerPos) {
    return { success: false, error: 'NOT_ON_FIELD' };
  }
  
  // 建立新狀態
  let newState = { ...state };
  
  // 從戰場移除乘客
  newState = removeFromBattlefield(newState, passengerPos);
  
  // 將乘客加入運輸單位
  const transportPos = getCardPosition(state, transportId);
  if (transportPos) {
    newState = addPassengerToTransport(newState, transportPos, passenger);
  }
  
  return { success: true, newState };
}

/**
 * 執行下車
 */
export function disembarkUnits(
  state: GameState,
  transportId: string,
  count: number
): DisembarkResult {
  const transport = findCardOnBattlefield(state, transportId);
  
  if (!transport) {
    return { success: false, error: 'NO_PASSENGERS' };
  }
  
  const passengers = transport.passengers ?? [];
  if (passengers.length < count) {
    return { success: false, error: 'NOT_ENOUGH_PASSENGERS' };
  }
  
  const transportPos = getCardPosition(state, transportId);
  if (!transportPos) {
    return { success: false, error: 'NO_PASSENGERS' };
  }
  
  // 找到空位放置下車的單位
  const emptySlots = findEmptySlots(state, transportPos.side);
  if (emptySlots.length < count) {
    return { success: false, error: 'NO_EMPTY_SLOTS' };
  }
  
  let newState = { ...state };
  const disembarkedUnits: Card[] = [];
  
  // 依序下車
  for (let i = 0; i < count; i++) {
    const passenger = passengers[i];
    const slot = emptySlots[i];
    
    // 放置到戰場
    newState = placeUnitOnBattlefield(newState, passenger, slot);
    disembarkedUnits.push(passenger);
  }
  
  // 從運輸單位移除乘客
  newState = removePassengersFromTransport(newState, transportPos, count);
  
  return { success: true, disembarkedUnits, newState };
}

/**
 * 召喚時自動搭乘小旅人
 */
export function autoBoard(
  state: GameState,
  transportId: string,
  capacity: number,
  playerId: string
): GameState {
  let newState = state;
  
  // 找到場上的小旅人
  const travelers = findTravelersOnField(state, playerId);
  
  // 依序搭乘直到滿載
  let boarded = 0;
  for (const traveler of travelers) {
    if (boarded >= capacity) break;
    
    const result = boardUnit(newState, traveler.id, transportId, capacity);
    if (result.success && result.newState) {
      newState = result.newState;
      boarded++;
    }
  }
  
  log.debug(`運輸單位 ${transportId} 自動搭乘了 ${String(boarded)} 個小旅人`);
  
  return newState;
}

// === 輔助函數 ===

function removeFromBattlefield(state: GameState, position: BattlefieldPosition): GameState {
  const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';
  
  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];
  newLine[position.column - 1] = { ...newLine[position.column - 1], unit: null };
  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;
  
  return { ...state, battlefield: newBattlefield };
}

function addPassengerToTransport(
  state: GameState,
  transportPos: BattlefieldPosition,
  passenger: Card
): GameState {
  const sideKey = transportPos.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = transportPos.row === 'front' ? 'frontLine' : 'backLine';
  
  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];
  
  const slot = newLine[transportPos.column - 1];
  if (slot.unit) {
    const passengers = slot.unit.passengers ?? [];
    newLine[transportPos.column - 1] = {
      ...slot,
      unit: {
        ...slot.unit,
        passengers: [...passengers, { ...passenger, location: 'battlefield' }],
      },
    };
  }
  
  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;
  
  return { ...state, battlefield: newBattlefield };
}

function removePassengersFromTransport(
  state: GameState,
  transportPos: BattlefieldPosition,
  count: number
): GameState {
  const sideKey = transportPos.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = transportPos.row === 'front' ? 'frontLine' : 'backLine';
  
  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];
  
  const slot = newLine[transportPos.column - 1];
  if (slot.unit?.passengers) {
    newLine[transportPos.column - 1] = {
      ...slot,
      unit: {
        ...slot.unit,
        passengers: slot.unit.passengers.slice(count),
      },
    };
  }
  
  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;
  
  return { ...state, battlefield: newBattlefield };
}

function findEmptySlots(state: GameState, side: 'player1' | 'player2'): BattlefieldPosition[] {
  const slots: BattlefieldPosition[] = [];
  const battleSide = side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;
  
  for (let i = 0; i < battleSide.frontLine.length; i++) {
    if (!battleSide.frontLine[i].unit) {
      slots.push({ side, row: 'front', column: (i + 1) as 1 | 2 | 3 | 4 | 5 });
    }
  }
  for (let i = 0; i < battleSide.backLine.length; i++) {
    if (!battleSide.backLine[i].unit) {
      slots.push({ side, row: 'back', column: (i + 1) as 1 | 2 | 3 | 4 | 5 });
    }
  }
  
  return slots;
}

function placeUnitOnBattlefield(
  state: GameState,
  unit: Card,
  position: BattlefieldPosition
): GameState {
  const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';
  
  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];
  
  newLine[position.column - 1] = {
    ...newLine[position.column - 1],
    unit: { ...unit, location: 'battlefield', position, orientation: 'upright' },
  };
  
  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;
  
  return { ...state, battlefield: newBattlefield };
}

function findTravelersOnField(state: GameState, playerId: string): Card[] {
  const travelers: Card[] = [];
  const side = playerId === state.player1.id 
    ? state.battlefield.player1Side 
    : state.battlefield.player2Side;
  
  for (const slot of [...side.frontLine, ...side.backLine]) {
    if (slot.unit && canBePassenger(slot.unit) && slot.unit.ownerId === playerId) {
      travelers.push(slot.unit);
    }
  }
  
  return travelers;
}

// 匯出為物件
export const TransportSystem = {
  parseTransportCapacity,
  parseDisembarkCount,
  isTransportUnit,
  canBePassenger,
  getTransportState,
  boardUnit,
  disembarkUnits,
  autoBoard,
};
